/**
 * CREATIVE DETONATOR - Version History Module
 * v5 - Fixed canvas update bug, added duplicate, UI tweaks
 * v6 - Fixed video handling: uses video preview modal, updates preloadedVideos cache
 *
 * Features:
 * - History button on idea cards (media section)
 * - Version history modal with compact design
 * - Uses existing image-preview.js for previews
 * - Download specific versions
 * - Delete unwanted versions
 * - Duplicate versions (make copy)
 * - In-place canvas update (no page reload when in canvas view)
 */

import { api } from '../../../js/api/client.js';
import { openPreview } from '../../../js/shared/image-preview.js';
import { GeneratorState, persistSessionState } from '../core/state.js';
import { Icons } from '../../../js/shared/icons.js';
import { escapeHtml } from '../../../js/core/sanitize.js';
import { onDocument, cleanupScope } from '../../../js/core/events.js';

// ============================================================================
// STATE
// ============================================================================

let currentIdeaId = null;      // Database UUID (for API calls)
let originalIdeaId = null;     // Original ID passed in (for cache lookups - may be frontend ID)
let currentVersions = [];
let modalInjected = false;

// ============================================================================
// MODAL HTML
// ============================================================================

const MODAL_HTML = `
<div id="version-history-modal" class="modal-overlay version-history-overlay">
  <div class="version-history-modal-content">
    <div class="version-history-header">
      <div class="version-history-close"></div>
      <h3 class="modal-title">VERSION HISTORY</h3>
    </div>
    <div class="version-history-body">
      <div id="version-history-loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading history...</p>
      </div>
      <div id="version-history-empty" class="empty-state hidden">
        <p>No version history yet.</p>
        <p class="hint">Versions are created when you refine, edit, or regenerate images/videos.</p>
      </div>
      <div id="version-history-list" class="version-list"></div>
    </div>
  </div>
</div>
`;

// ============================================================================
// INITIALIZATION
// ============================================================================

export function initVersionHistory() {
  if (window._versionHistoryInitialized) return;
  window._versionHistoryInitialized = true;

  injectModal();
  document.body.addEventListener('click', handleHistoryClick);
  initModalEvents();

  console.log('[VersionHistory] Initialized v5');
}

function injectModal() {
  if (modalInjected) return;
  const container = document.createElement('div');
  container.innerHTML = MODAL_HTML;
  document.body.appendChild(container.firstElementChild);
  modalInjected = true;
}

function handleHistoryClick(e) {
  const historyBtn = e.target.closest('.btn-version-history');
  if (historyBtn) {
    e.stopPropagation();
    const ideaId = historyBtn.dataset.ideaId;
    if (ideaId && ideaId.length > 30 && ideaId.includes('-')) {
      openVersionHistory(ideaId);
    }
  }
}

function initModalEvents() {
  const modal = document.getElementById('version-history-modal');
  if (!modal) return;

  modal.querySelector('.version-history-close')?.addEventListener('click', closeVersionHistory);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeVersionHistory();
  });

  onDocument('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('active')) {
      closeVersionHistory();
    }
  }, 'version-history');
}

// ============================================================================
// MODAL FUNCTIONS
// ============================================================================

export async function openVersionHistory(ideaId) {
  // ═══════════════════════════════════════════════════════════════════
  // FIX: Resolve frontend ID to database UUID
  // Frontend IDs like "A1-1735..." must be mapped to DB UUIDs
  // Pattern copied from image-generation.js saveImageToDatabase()
  // ═══════════════════════════════════════════════════════════════════
  let dbId = ideaId;

  // Try in-memory mapping first
  if (GeneratorState.idMapping && GeneratorState.idMapping[ideaId]) {
    dbId = GeneratorState.idMapping[ideaId];
    console.log('[VersionHistory] Resolved ID:', ideaId, '→', dbId);
  } else {
    // Fallback to sessionStorage
    try {
      const stored = sessionStorage.getItem('cd_id_mapping');
      if (stored) {
        const mapping = JSON.parse(stored);
        if (mapping[ideaId]) {
          dbId = mapping[ideaId];
          console.log('[VersionHistory] Resolved from storage:', ideaId, '→', dbId);
        }
      }
    } catch (e) {
      console.warn('[VersionHistory] Could not read id mapping:', e);
    }
  }

  originalIdeaId = ideaId;  // Store original (for cache lookups)
  currentIdeaId = dbId;     // Store resolved DB UUID (for API calls)

  const modal = document.getElementById('version-history-modal');
  const listEl = document.getElementById('version-history-list');
  const loadingEl = document.getElementById('version-history-loading');
  const emptyEl = document.getElementById('version-history-empty');

  if (!modal || !listEl) return;

  modal.classList.add('active');
  loadingEl?.classList.remove('hidden');
  emptyEl?.classList.add('hidden');
  listEl.innerHTML = '';

  try {
    const result = await api.getIdeaVersions(dbId);
    if (!result.success) throw new Error(result.error || 'Failed to load');

    currentVersions = result.versions || [];
    const currentMedia = getCurrentIdeaMedia(ideaId);
    renderVersionList(currentVersions, currentMedia);
  } catch (error) {
    console.error('[VersionHistory] Load error:', error);
    listEl.innerHTML = `<div class="version-error"><p>Failed to load</p><p class="error-detail">${error.message}</p></div>`;
  } finally {
    loadingEl?.classList.add('hidden');
  }
}

function getCurrentIdeaMedia(ideaId) {
  let imageUrl = null;
  let videoUrl = null;

  // 1. Check canvas (if open)
  if (window.IdeaCanvas?.isOpen()) {
    const idea = window.IdeaCanvas.getCurrentIdea();
    if (idea?.id === ideaId) {
      imageUrl = idea.image_url || idea.imageUrl;
      videoUrl = idea.video_url || idea.videoUrl;
    }
  }

  // 2. Check preloaded images cache
  if (!imageUrl && GeneratorState?.preloadedImages?.[ideaId]) {
    imageUrl = GeneratorState.preloadedImages[ideaId];
  }

  // 3. Check generated ideas (Generator context)
  if (!imageUrl && GeneratorState?.generatedIdeas) {
    for (const group of GeneratorState.generatedIdeas) {
      const idea = group.ideas?.find(i => i.id === ideaId);
      if (idea) {
        imageUrl = idea.imageUrl || idea.image_url;
        videoUrl = idea.videoUrl || idea.video_url;
        break;
      }
    }
  }

  // 4. Check Dashboard Ideas (Dashboard context - after refresh)
  if (!imageUrl && window.Ideas?.items) {
    const idea = window.Ideas.items.find(i => i.id === ideaId);
    if (idea) {
      imageUrl = idea.image_url || idea.imageUrl;
      videoUrl = idea.video_url || idea.videoUrl;
    }
  }

  return { imageUrl, videoUrl };
}

// Normalize URL for comparison (strip query params, trailing slashes)
function normalizeUrl(url) {
  if (!url) return '';
  try {
    const u = new URL(url);
    // Remove query string and hash, normalize path
    return u.origin + u.pathname.replace(/\/$/, '');
  } catch {
    return url.split('?')[0].replace(/\/$/, '');
  }
}

export function closeVersionHistory() {
  const modal = document.getElementById('version-history-modal');
  modal?.classList.remove('active');
  currentIdeaId = null;
  originalIdeaId = null;
  currentVersions = [];
  cleanupScope('version-history');
}

// ============================================================================
// RENDER
// ============================================================================

function renderVersionList(versions, currentMedia) {
  const listEl = document.getElementById('version-history-list');
  const emptyEl = document.getElementById('version-history-empty');
  if (!listEl) return;

  if (versions.length === 0) {
    listEl.innerHTML = '';
    emptyEl?.classList.remove('hidden');
    return;
  }

  emptyEl?.classList.add('hidden');

  // ═══════════════════════════════════════════════════════════════════
  // FIX: Only first URL match shows CURRENT badge
  // Prevents all duplicates from showing as current (they have same URL)
  // ═══════════════════════════════════════════════════════════════════
  let foundCurrent = false;

  listEl.innerHTML = versions.map(v => {
    const hasMedia = v.video_url || v.image_url;
    const isVideo = !!v.video_url;
    const mediaUrl = v.video_url || v.image_url || '';

    // Check if URL matches current media
    const urlMatches = (v.image_url && normalizeUrl(v.image_url) === normalizeUrl(currentMedia.imageUrl)) ||
                       (v.video_url && normalizeUrl(v.video_url) === normalizeUrl(currentMedia.videoUrl));

    // Only FIRST match is current (list sorted desc by version_number)
    const isCurrent = urlMatches && !foundCurrent;
    if (isCurrent) foundCurrent = true;

    return `
    <div class="version-item ${isCurrent ? 'is-current' : ''}" data-version="${v.version_number}">
      <div class="version-row">
        <div class="version-thumb ${hasMedia ? 'has-media' : ''}" ${hasMedia ? `data-url="${mediaUrl}" data-video="${isVideo}"` : ''}>
          ${v.video_url
            ? `<video src="${v.video_url}" class="version-thumb-video" muted preload="metadata"></video>`
            : (v.image_url
              ? `<img src="${v.image_url}" class="version-thumb-image" alt="v${v.version_number}" onerror="this.parentElement.classList.add('broken')">`
              : `<div class="version-thumb-empty">T</div>`)}
        </div>

        <div class="version-info">
          <div class="version-meta">
            ${hasMedia ? `<span class="version-media-icon">${isVideo ? Icons.video({ size: 12 }) : Icons.image({ size: 12 })}</span>` : ''}
            <span class="version-number">v${v.version_number}</span>
            ${isCurrent ? '<span class="version-current-badge">CURRENT</span>' : ''}
            <span class="version-date">${formatDate(v.created_at)}</span>
          </div>
          <div class="version-headline">"${escapeHtml(v.content?.headline || v.content?.title || 'Untitled')}"</div>
          <div class="version-change">${formatChangeDescription(v.change_type, v.change_description)}</div>
        </div>

        <div class="version-actions">
          ${hasMedia ? `
            <button class="btn-version-action btn-version-preview" data-url="${mediaUrl}" data-video="${isVideo}" title="Preview">
              ${Icons.fullscreen16({ size: 12 })}
            </button>
            <button class="btn-version-action btn-version-download" data-url="${mediaUrl}" data-version="${v.version_number}" data-video="${isVideo}" title="Download">
              ${Icons.download({ size: 12 })}
            </button>
          ` : ''}
          <button class="btn-version-action btn-version-duplicate" data-version="${v.version_number}" title="Duplicate">
            ${Icons.duplicate({ size: 12 })}
          </button>
          <button class="btn-version-action btn-version-delete" data-version="${v.version_number}" title="Delete">
            ${Icons.delete({ size: 12 })}
          </button>
          ${!isCurrent ? `<button class="btn-version-use" data-version="${v.version_number}">USE THIS</button>` : ''}
        </div>
      </div>
    </div>
  `;
  }).join('');

  bindVersionActions(listEl);
}

function bindVersionActions(container) {
  // Preview
  container.querySelectorAll('.btn-version-preview').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const url = btn.dataset.url;
      const isVideo = btn.dataset.video === 'true';
      if (isVideo) {
        // Use video preview modal instead of new tab
        if (window.openVideoPreview) {
          window.openVideoPreview(url);
        } else {
          window.open(url, '_blank');
        }
      } else {
        openPreview(url);
      }
    });
  });

  // Thumbnail preview
  container.querySelectorAll('.version-thumb.has-media').forEach(thumb => {
    thumb.addEventListener('click', (e) => {
      e.stopPropagation();
      const url = thumb.dataset.url;
      const isVideo = thumb.dataset.video === 'true';
      if (isVideo) {
        // Use video preview modal instead of new tab
        if (window.openVideoPreview) {
          window.openVideoPreview(url);
        } else {
          window.open(url, '_blank');
        }
      } else {
        openPreview(url);
      }
    });
  });

  // Download
  container.querySelectorAll('.btn-version-download').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      downloadMedia(btn.dataset.url, btn.dataset.version, btn.dataset.video === 'true');
    });
  });

  // Duplicate
  container.querySelectorAll('.btn-version-duplicate').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      duplicateVersion(parseInt(btn.dataset.version));
    });
  });

  // Delete
  container.querySelectorAll('.btn-version-delete').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      deleteVersion(parseInt(btn.dataset.version));
    });
  });

  // Use This
  container.querySelectorAll('.btn-version-use').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      restoreVersion(parseInt(btn.dataset.version));
    });
  });
}

// ============================================================================
// ACTIONS
// ============================================================================

function downloadMedia(url, versionNumber, isVideo = false) {
  const ext = isVideo ? 'mp4' : 'jpg';
  const a = document.createElement('a');
  a.href = url;
  a.download = `idea-v${versionNumber}.${ext}`;
  a.target = '_blank';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.Toast?.success('Download started');
}

async function deleteVersion(versionNumber) {
  if (!currentIdeaId) return;

  const confirmed = window.Modal?.confirm
    ? await new Promise(resolve => {
        window.Modal.confirm({
          title: 'Delete Version',
          message: `Delete version ${versionNumber}? This cannot be undone.`,
          confirmText: 'Delete',
          confirmClass: 'btn-danger',
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false)
        });
      })
    : window.confirm(`Delete version ${versionNumber}?`);

  if (!confirmed) return;

  const btn = document.querySelector(`.btn-version-delete[data-version="${versionNumber}"]`);
  if (btn) btn.disabled = true;

  try {
    const result = await api.deleteIdeaVersion(currentIdeaId, versionNumber);
    if (!result.success) throw new Error(result.error || 'Delete failed');

    currentVersions = currentVersions.filter(v => v.version_number !== versionNumber);
    const currentMedia = getCurrentIdeaMedia(currentIdeaId);
    renderVersionList(currentVersions, currentMedia);

    window.Toast?.success('Version deleted');
    window.Sounds?.play('success');
  } catch (error) {
    console.error('[VersionHistory] Delete error:', error);
    alert('Failed to delete: ' + error.message);
    if (btn) btn.disabled = false;
  }
}

async function duplicateVersion(versionNumber) {
  if (!currentIdeaId) return;

  const btn = document.querySelector(`.btn-version-duplicate[data-version="${versionNumber}"]`);
  if (btn) btn.disabled = true;

  try {
    const result = await api.duplicateIdeaVersion(currentIdeaId, versionNumber);
    if (!result.success) throw new Error(result.error || 'Duplicate failed');

    // Add new version to list and re-render
    if (result.version) {
      currentVersions.unshift(result.version);
      const currentMedia = getCurrentIdeaMedia(currentIdeaId);
      renderVersionList(currentVersions, currentMedia);
    }

    window.Toast?.success(`Copied v${versionNumber} → v${result.version?.version_number}`);
    window.Sounds?.play('success');
  } catch (error) {
    console.error('[VersionHistory] Duplicate error:', error);
    alert('Failed to duplicate: ' + error.message);
    if (btn) btn.disabled = false;
  }
}

async function restoreVersion(versionNumber) {
  if (!currentIdeaId) return;

  // CRITICAL: Save both IDs BEFORE closing modal (which clears them)
  const dbId = currentIdeaId;           // Database UUID for API calls
  const frontendId = originalIdeaId;    // Original ID for cache lookups (may be same as dbId)

  const btn = document.querySelector(`.btn-version-use[data-version="${versionNumber}"]`);
  if (btn) {
    btn.disabled = true;
    btn.textContent = '...';
  }

  try {
    const result = await api.restoreIdeaVersion(dbId, versionNumber);
    if (!result.success) throw new Error(result.error || 'Restore failed');

    const newImageUrl = result.idea?.image_url;
    const newVideoUrl = result.idea?.video_url;

    console.log('[VersionHistory] Switching to v' + versionNumber, { newImageUrl, newVideoUrl, dbId, frontendId });

    // 1. Update GeneratorState preloadedImages cache (try both IDs)
    if (newImageUrl && GeneratorState?.preloadedImages) {
      GeneratorState.preloadedImages[dbId] = newImageUrl;
      if (frontendId && frontendId !== dbId) {
        GeneratorState.preloadedImages[frontendId] = newImageUrl;
      }
      console.log('[VersionHistory] Updated preloadedImages cache');
    }

    // 1b. Update GeneratorState preloadedVideos cache (try both IDs)
    if (GeneratorState) {
      if (!GeneratorState.preloadedVideos) {
        GeneratorState.preloadedVideos = {};
      }
      if (newVideoUrl) {
        GeneratorState.preloadedVideos[dbId] = newVideoUrl;
        if (frontendId && frontendId !== dbId) {
          GeneratorState.preloadedVideos[frontendId] = newVideoUrl;
        }
        console.log('[VersionHistory] Updated preloadedVideos cache');
      } else {
        // Clear video URL if restoring to a version without video
        delete GeneratorState.preloadedVideos[dbId];
        if (frontendId && frontendId !== dbId) {
          delete GeneratorState.preloadedVideos[frontendId];
        }
      }
    }

    // Task 102: Update latestMediaTimestamp based on restored version's media
    // This ensures the card shows the correct media type after restore
    if (GeneratorState) {
      if (!GeneratorState.latestMediaTimestamp) GeneratorState.latestMediaTimestamp = {};
      const mediaType = newVideoUrl ? 'video' : (newImageUrl ? 'image' : null);
      if (mediaType) {
        GeneratorState.latestMediaTimestamp[String(dbId)] = {
          type: mediaType,
          timestamp: Date.now()
        };
        if (frontendId && frontendId !== dbId) {
          GeneratorState.latestMediaTimestamp[String(frontendId)] = {
            type: mediaType,
            timestamp: Date.now()
          };
        }
        // Persist immediately
        persistSessionState();
        console.log('[VersionHistory] Updated latestMediaTimestamp to', mediaType);
      }
    }

    // 2. Update idea in GeneratorState.generatedIdeas (match both IDs)
    if (GeneratorState?.generatedIdeas) {
      for (const group of GeneratorState.generatedIdeas) {
        const idea = group.ideas?.find(i =>
          String(i.id) === String(dbId) || String(i.id) === String(frontendId)
        );
        if (idea) {
          idea.image_url = newImageUrl;
          idea.imageUrl = newImageUrl;
          idea.video_url = newVideoUrl;
          idea.videoUrl = newVideoUrl;
          console.log('[VersionHistory] Updated generatedIdeas');
          break;
        }
      }
    }

    // Close modal AFTER updating caches
    closeVersionHistory();

    // 3. Update grid card if visible (try both IDs)
    updateGridCardThumbnail(frontendId || dbId, newImageUrl, newVideoUrl);
    if (frontendId && frontendId !== dbId) {
      updateGridCardThumbnail(dbId, newImageUrl, newVideoUrl);
    }

    // 4. Check if canvas is open and update in-place
    if (window.IdeaCanvas?.isOpen()) {
      const currentIdea = window.IdeaCanvas.getCurrentIdea();
      console.log('[VersionHistory] Canvas is open, current idea:', currentIdea?.id);

      // FIX: Compare against both IDs using String() normalization
      const canvasId = String(currentIdea?.id || '');
      const matchesDb = canvasId === String(dbId);
      const matchesFrontend = frontendId && canvasId === String(frontendId);

      if (currentIdea && (matchesDb || matchesFrontend)) {
        // Update the idea object
        currentIdea.image_url = newImageUrl;
        currentIdea.imageUrl = newImageUrl;
        currentIdea.video_url = newVideoUrl;
        currentIdea.videoUrl = newVideoUrl;

        // Re-render canvas
        window.IdeaCanvas.updateIdea(currentIdea);
        console.log('[VersionHistory] Canvas updated in-place');

        window.Toast?.success(`Switched to v${versionNumber}`);
        window.Sounds?.play('success');
        return; // Don't reload!
      }
    }

    window.Toast?.success(`Switched to v${versionNumber}`);

    // Not in canvas - reload
    window.location.reload();

  } catch (error) {
    console.error('[VersionHistory] Restore error:', error);
    alert('Failed to switch: ' + error.message);
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'USE THIS';
    }
  }
}

function updateGridCardThumbnail(ideaId, imageUrl, videoUrl) {
  // Try multiple selectors (Generator uses data-id, Dashboard might use data-idea-id)
  const card = document.querySelector(`.idea-card[data-id="${ideaId}"]`) ||
               document.querySelector(`[data-idea-id="${ideaId}"]`);
  if (!card) {
    console.log('[VersionHistory] Grid card not found for:', ideaId);
    return;
  }

  // Update image
  if (imageUrl) {
    // Try updating just the img src first (faster)
    const img = card.querySelector('.generated-image') || card.querySelector('.idea-image');
    if (img) {
      img.src = imageUrl;
      console.log('[VersionHistory] Updated grid card image');
    }

    // Also update preview/download button data-url attributes
    const previewBtn = card.querySelector('.btn-preview');
    if (previewBtn) previewBtn.dataset.url = imageUrl;
    const downloadBtn = card.querySelector('.btn-download');
    if (downloadBtn) downloadBtn.dataset.url = imageUrl;
    const makeVideoBtn = card.querySelector('.btn-make-video');
    if (makeVideoBtn) makeVideoBtn.dataset.imageUrl = imageUrl;
  }

  // Update video
  if (videoUrl) {
    const video = card.querySelector('.idea-video') || card.querySelector('video');
    if (video) {
      video.src = videoUrl;
      console.log('[VersionHistory] Updated grid card video');
    }
  }
}

// ============================================================================
// HELPERS
// ============================================================================

function formatChangeDescription(type, description) {
  // If there's a custom description (like "Copied from v3"), use it
  if (description && description.startsWith('Copied from')) {
    return description;
  }
  const labels = {
    'initial': 'Created',
    'refinement': 'Refined',
    'image_generation': 'Image generated',
    'image_edit': 'Image edited',
    'video_generation': 'Video generated',
    'manual_edit': 'Edited',
    'restore': 'Restored'
  };
  return labels[type] || type || '';
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// escapeHtml imported from ../../core/sanitize.js

// ============================================================================
// RENDER HELPER
// ============================================================================

export function renderHistoryButton(ideaId) {
  const isPersisted = ideaId && ideaId.length > 30 && ideaId.includes('-');
  if (!isPersisted) return '';

  return `
    <button class="btn-img-action btn-version-history" data-idea-id="${ideaId}" title="Version History">
      ${Icons.history({ size: 14 })}
    </button>
  `;
}
